import { useState, useEffect } from 'react';
import {
    Download,
    Loader2,
    X
} from 'lucide-react';
import { useAdminStore } from '../../stores/adminStore';
import { transcriptService, downloadFile } from '../../services/transcriptService';

export function AdminTranscriptsPage() {
    const [selectedOrganizationYear, setSelectedOrganizationYear] = useState<string>('');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState('');
    const [showProgressModal, setShowProgressModal] = useState(false);
    const { employees, isLoading, error, fetchEmployeesOnly } = useAdminStore();

    useEffect(() => {
        // Only fetch if employees data is not already available
        if (!employees || employees.length === 0) {
            fetchEmployeesOnly();
        }
    }, []); // Empty dependency array to only run once

    const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId);

    const handleDownloadOrganizationTranscript = async () => {
        try {
            setIsGenerating(true);
            setShowProgressModal(true);
            setProgress(0);
            setStatusMessage('Starting organization transcript generation...');
            
            // Start organization transcript generation
            const { jobId } = await transcriptService.startOrganizationTranscriptGeneration(selectedOrganizationYear);
            setStatusMessage('Processing organization data...');
            
            // Poll for completion
            const pollForCompletion = async () => {
                try {
                    const status = await transcriptService.checkOrganizationTranscriptStatus(jobId);
                    
                    if (status.status === 'completed') {
                        setStatusMessage('Downloading file...');
                        
                        try {
                            // Download the organization transcript file
                            const { blob, filename } = await transcriptService.downloadOrganizationTranscript(jobId);
                            
                            downloadFile(blob, filename);
                            
                            setIsGenerating(false);
                            setProgress(100);
                            setStatusMessage('Download complete!');
                            
                            // Close modal after 2 seconds
                            setTimeout(() => {
                                setShowProgressModal(false);
                                setStatusMessage('');
                                setProgress(0);
                            }, 2000);
                        } catch (downloadError: any) {
                            setIsGenerating(false);
                            const errorMsg = downloadError.message || downloadError.response?.data?.message || downloadError.response?.statusText || 'Unknown error';
                            setStatusMessage(`Error downloading file: ${errorMsg}`);
                            setProgress(0);
                            console.error('Error downloading organization transcript:', downloadError);
                            
                            setTimeout(() => {
                                setShowProgressModal(false);
                                setStatusMessage('');
                                setProgress(0);
                            }, 3000);
                        }
                        
                    } else if (status.status === 'failed') {
                        setIsGenerating(false);
                        setStatusMessage(`Error: ${status.message}`);
                        setProgress(0);
                        
                        // Close modal after 3 seconds on error
                        setTimeout(() => {
                            setShowProgressModal(false);
                            setStatusMessage('');
                            setProgress(0);
                        }, 3000);
                    } else {
                        // Still processing
                        setProgress(status.progress || 0);
                        setStatusMessage(status.message || 'Processing...');
                        setTimeout(pollForCompletion, 2000);
                    }
                } catch (error) {
                    setIsGenerating(false);
                    setStatusMessage('Error checking status');
                    console.error('Error checking status:', error);
                    
                    // Close modal after 3 seconds on error
                    setTimeout(() => {
                        setShowProgressModal(false);
                        setStatusMessage('');
                        setProgress(0);
                    }, 3000);
                }
            };
            
            // Start polling
            pollForCompletion();
            
        } catch (error) {
            setIsGenerating(false);
            setStatusMessage('Failed to start organization transcript generation');
            console.error('Failed to start organization transcript generation:', error);
            
            setTimeout(() => {
                setShowProgressModal(false);
                setStatusMessage('');
                setProgress(0);
            }, 3000);
        }
    };

    const handleDownloadTranscript = async () => {
        if (!selectedEmployeeId) return;
        
        try {
            setIsGenerating(true);
            setShowProgressModal(true);
            setProgress(0);
            setStatusMessage('Starting transcript generation...');
            
            // Start generation
            const { jobId } = await transcriptService.startTranscriptGeneration([selectedEmployeeId]);
            setStatusMessage('Processing employee data...');
            
            // Poll for completion
            const pollForCompletion = async () => {
                try {
                    const status = await transcriptService.checkStatus(jobId);
                    
                    if (status.status === 'completed') {
                        setStatusMessage('Downloading file...');
                        
                        try {
                            // Get transcript info to get the actual download URL and filename
                            const transcriptInfo = await transcriptService.getTranscriptInfo(jobId);
                            
                            if (transcriptInfo.transcripts && transcriptInfo.transcripts.length > 0) {
                                // Download each transcript file
                                for (const transcript of transcriptInfo.transcripts) {
                                    try {
                                        const blob = await transcriptService.downloadTranscript(jobId, transcript.filename);
                                        downloadFile(blob, transcript.filename);
                                    } catch (downloadError: any) {
                                        console.error('Error downloading transcript file:', downloadError);
                                        setIsGenerating(false);
                                        const errorMsg = downloadError.message || downloadError.response?.data?.message || downloadError.response?.statusText || 'Unknown error';
                                        setStatusMessage(`Error downloading file: ${errorMsg}`);
                                        setProgress(0);
                                        
                                        setTimeout(() => {
                                            setShowProgressModal(false);
                                            setStatusMessage('');
                                            setProgress(0);
                                        }, 3000);
                                        return;
                                    }
                                }
                                
                                setIsGenerating(false);
                                setProgress(100);
                                setStatusMessage('Download complete!');
                                
                                // Close modal after 2 seconds
                                setTimeout(() => {
                                    setShowProgressModal(false);
                                    setStatusMessage('');
                                    setProgress(0);
                                }, 2000);
                            } else {
                                setIsGenerating(false);
                                setStatusMessage('Error: No transcripts found in response');
                                setProgress(0);
                                console.error('No transcripts found in response:', transcriptInfo);
                                
                                setTimeout(() => {
                                    setShowProgressModal(false);
                                    setStatusMessage('');
                                    setProgress(0);
                                }, 3000);
                            }
                        } catch (infoError: any) {
                            setIsGenerating(false);
                            const errorMsg = infoError.message || infoError.response?.data?.message || infoError.response?.statusText || 'Unknown error';
                            setStatusMessage(`Error getting transcript info: ${errorMsg}`);
                            setProgress(0);
                            console.error('Error getting transcript info:', infoError);
                            
                            setTimeout(() => {
                                setShowProgressModal(false);
                                setStatusMessage('');
                                setProgress(0);
                            }, 3000);
                        }
                        
                    } else if (status.status === 'failed') {
                        setIsGenerating(false);
                        setStatusMessage(`Error: ${status.message}`);
                        setProgress(0);
                        
                        // Close modal after 3 seconds on error
                        setTimeout(() => {
                            setShowProgressModal(false);
                            setStatusMessage('');
                            setProgress(0);
                        }, 3000);
                    } else {
                        // Still processing
                        setProgress(status.progress || 0);
                        setStatusMessage(status.message || 'Processing...');
                        setTimeout(pollForCompletion, 2000);
                    }
                } catch (error) {
                    setIsGenerating(false);
                    setStatusMessage('Error checking status');
                    console.error('Error checking status:', error);
                    
                    // Close modal after 3 seconds on error
                    setTimeout(() => {
                        setShowProgressModal(false);
                        setStatusMessage('');
                        setProgress(0);
                    }, 3000);
                }
            };
            
            // Start polling
            pollForCompletion();
            
        } catch (error) {
            setIsGenerating(false);
            setStatusMessage('Failed to start transcript generation');
            console.error('Failed to start transcript generation:', error);
            
            // Close modal after 3 seconds on error
            setTimeout(() => {
                setShowProgressModal(false);
                setStatusMessage('');
                setProgress(0);
            }, 3000);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center space-x-2">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                    <span className="text-slate-600">Loading employees...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                <h1 className="text-2xl font-bold text-foreground">Download Transcript</h1>
            </div> */}

            {/* Organization Transcript Section */}
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md">
                <h1 className="text-2xl font-bold text-foreground pb-4">Organization Transcript</h1>
                <div className="space-y-4">
                    <div>
                        <select
                            id="organization-select"
                            value={selectedOrganizationYear}
                            onChange={(e) => setSelectedOrganizationYear(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                            <option value="">Choose a year</option>
                            <option value="2025">2025</option>
                        </select>
                    </div>

                    <button
                        onClick={handleDownloadOrganizationTranscript}
                        disabled={!selectedOrganizationYear || isGenerating}
                        className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {isGenerating ? 'Generating...' : 'Download Organization Transcript'}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md">
                <h1 className="text-2xl font-bold text-foreground pb-4">Single Employee Transcript</h1>
                <div className="space-y-4">
                    <div>
                        <select
                            id="employee-select"
                            value={selectedEmployeeId}
                            onChange={(e) => setSelectedEmployeeId(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                            <option value="">Choose an employee...</option>
                            {employees.map((employee) => (
                                <option key={employee.id} value={employee.id}>
                                    {employee.name} ({employee.email})
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        onClick={handleDownloadTranscript}
                        disabled={!selectedEmployeeId || isGenerating}
                        className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {isGenerating ? 'Generating...' : 'Download Transcript'}
                    </button>

                    {selectedEmployee && (
                        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                            <h3 className="text-sm font-medium text-foreground">Selected Employee:</h3>
                            <p className="text-sm text-slate-600">{selectedEmployee.name}</p>
                            <p className="text-xs text-slate-500">{selectedEmployee.email}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Progress Modal */}
            {showProgressModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-foreground">Generating Transcript</h3>
                            {!isGenerating && (
                                <button
                                    onClick={() => setShowProgressModal(false)}
                                    className="text-slate-400 hover:text-slate-600"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Progress</span>
                                <span className="text-sm font-medium text-foreground">{progress}%</span>
                            </div>
                            
                            <div className="w-full bg-slate-200 rounded-full h-2">
                                <div 
                                    className="bg-primary-600 h-2 rounded-full transition-all duration-300" 
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            
                            <p className="text-sm text-foreground">{statusMessage}</p>
                            
                            {isGenerating && (
                                <p className="text-xs text-slate-500">Estimated time: 10-30 seconds</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

